import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Info, LineChart } from 'lucide-react';
import { AnalyticsPeriod, ResolvedPeriod } from '../../PeriodPicker';
import { DynamicsControls } from './DynamicsControls';
import {
  DynamicsChartId,
  DynamicsPrefs,
  loadDynamicsPrefs,
  saveDynamicsPrefs,
} from './dynamicsPrefs';
import { DynamicsA_PlanDoneDebtVF } from './DynamicsA_PlanDoneDebtVF';
import { DynamicsB_BugCumulativeFlow } from './DynamicsB_BugCumulativeFlow';
import { DynamicsC_BugArrivalVsClosure } from './DynamicsC_BugArrivalVsClosure';
import { DynamicsD_MTTRDynamics } from './DynamicsD_MTTRDynamics';
import { DynamicsE_EscapedDefects } from './DynamicsE_EscapedDefects';
import { DynamicsG_QAThroughput } from './DynamicsG_QAThroughput';
import { DynamicsH_ReopenDynamics } from './DynamicsH_ReopenDynamics';
import { DynamicsI_SprintPredictability } from './DynamicsI_SprintPredictability';
import { DynamicsJ_BugOriginSplit } from './DynamicsJ_BugOriginSplit';
import { DynamicsK_EscapeTaxonomy } from './DynamicsK_EscapeTaxonomy';
import { CHART_ID_TO_METRIC, DynamicsBatchProvider } from './DynamicsBatchContext';

interface DynamicsSectionProps {
  period: AnalyticsPeriod | null;
  resolvedPeriod: ResolvedPeriod | null;
}

interface ChildProps {
  groupBy: 'sprint' | 'release' | 'week' | 'month';
  lastN: number;
  resolvedPeriod: ResolvedPeriod | null;
  onDataStatus?: (s: { metric: string; hasData: boolean; loading: boolean }) => void;
}

const CHART_COMPONENTS: Record<DynamicsChartId, React.ComponentType<ChildProps>> = {
  A: DynamicsA_PlanDoneDebtVF,
  B: DynamicsB_BugCumulativeFlow,
  C: DynamicsC_BugArrivalVsClosure,
  D: DynamicsD_MTTRDynamics,
  E: DynamicsE_EscapedDefects,
  G: DynamicsG_QAThroughput,
  H: DynamicsH_ReopenDynamics,
  I: DynamicsI_SprintPredictability,
  J: DynamicsJ_BugOriginSplit,
  K: DynamicsK_EscapeTaxonomy,
};

export const DynamicsSection: React.FC<DynamicsSectionProps> = ({ resolvedPeriod }) => {
  const [prefs, setPrefs] = useState<DynamicsPrefs>(() => loadDynamicsPrefs());
  const [cardStatus, setCardStatus] = useState<
    Record<string, { hasData: boolean; loading: boolean }>
  >({});

  useEffect(() => {
    saveDynamicsPrefs(prefs);
  }, [prefs]);

  const enabledMetrics = useMemo(
    () => prefs.enabled.map((id) => CHART_ID_TO_METRIC[id]),
    [prefs.enabled]
  );

  const onDataStatus = useCallback((s: { metric: string; hasData: boolean; loading: boolean }) => {
    setCardStatus((prev) => {
      const cur = prev[s.metric];
      if (cur && cur.hasData === s.hasData && cur.loading === s.loading) return prev;
      return { ...prev, [s.metric]: { hasData: s.hasData, loading: s.loading } };
    });
  }, []);

  const visibleChartIds = useMemo(() => {
    if (!prefs.hideEmpty) return prefs.enabled;
    return prefs.enabled.filter((id) => {
      const metric = CHART_ID_TO_METRIC[id];
      const st = cardStatus[metric];
      // Keep cards that are still loading or haven't reported yet so the user
      // doesn't see a flash of "no cards" on first mount.
      if (!st) return true;
      if (st.loading) return true;
      return st.hasData;
    });
  }, [prefs.enabled, prefs.hideEmpty, cardStatus]);

  const hiddenCount = prefs.enabled.length - visibleChartIds.length;

  return (
    <section className="dynamics-section">
      <header className="dynamics-section__header">
        <h2>
          <LineChart size={20} />
          Dynamics
          <span
            className="stat-hint-icon"
            title="Team rhythm over time. Toggle cards to build your own QA dashboard. Trend lines show direction; exact values matter less than slope."
            aria-label="About Dynamics"
          >
            <Info size={14} />
          </span>
        </h2>
        <p>Your saved selection is remembered locally. A, C and E are enabled by default.</p>
      </header>
      <DynamicsControls prefs={prefs} onChange={setPrefs} />
      {prefs.hideEmpty && hiddenCount > 0 && (
        <p
          className="quality-breakdown__empty"
          style={{ marginTop: 8, fontSize: 12 }}
          role="status"
        >
          {hiddenCount} empty card{hiddenCount === 1 ? '' : 's'} hidden. Uncheck{' '}
          <strong>Show only cards with data</strong> to reveal them.
        </p>
      )}
      <DynamicsBatchProvider
        metrics={enabledMetrics}
        groupBy={prefs.groupBy}
        lastN={prefs.lastN}
        resolvedPeriod={resolvedPeriod}
      >
        <div className="dynamics-grid">
          {visibleChartIds.map((id) => {
            const Component = CHART_COMPONENTS[id];
            if (!Component) return null;
            return (
              <Component
                key={id}
                groupBy={prefs.groupBy}
                lastN={prefs.lastN}
                resolvedPeriod={resolvedPeriod}
                onDataStatus={onDataStatus}
              />
            );
          })}
          {prefs.enabled.length === 0 && (
            <p className="quality-breakdown__empty">
              No dynamics cards selected. Pick one or more from the chip list above.
            </p>
          )}
        </div>
      </DynamicsBatchProvider>
    </section>
  );
};
